// Platform-branched checkout. One entry point for every "buy" CTA in
// the app; chooses the right rail behind the scenes:
//   - web  → Stripe Checkout (existing flow)
//   - ios  → Apple IAP via react-native-iap, verify on server
//   - android → Google Play Billing via react-native-iap, verify on server
//
// Apple's guideline 3.1.1 + Google Play's billing requirements mean
// digital subscriptions consumed in the app MUST use IAP on native.
// This hook is the single seam that enforces that.

import { useRouter } from "expo-router";
import { useState } from "react";
import { Platform } from "react-native";
import { TRPCClientError } from "@trpc/client";

import { trpc } from "../trpc";
import { iapBridge, type CheckoutTier } from "./iap-bridge";

// True when a thrown tRPC error is an auth failure — i.e. an anonymous
// user tried to purchase. Checked structurally first (never trust the
// message string alone), then by substring as a fallback.
function isUnauthorized(err: unknown): boolean {
  const code = (err as { data?: { code?: string } })?.data?.code;
  if (code === "UNAUTHORIZED") return true;
  const message = err instanceof Error ? err.message : String(err);
  return message.includes("UNAUTHORIZED");
}

// Item 7: Classification of react-native-iap error codes into distinct UX states.
// "cancel"       → silent no-op (user bailed from the native sheet)
// "conflict"     → wrong-account error with specific instructions
// "already-owned"→ prompt user to Restore Purchases
// "unavailable"  → transient store unavailability
// "generic"      → catch-all red banner
export type IapErrorKind = "cancel" | "conflict" | "already-owned" | "unavailable" | "generic";

function classifyIapError(msg: string): { message: string; kind: IapErrorKind } {
  if (/E_USER_CANCELLED|cancelled|canceled/i.test(msg)) {
    return { message: "", kind: "cancel" };
  }
  if (/E_PRODUCT_ALREADY_OWNED/i.test(msg)) {
    return {
      message:
        'You already own this. Tap "Restore Purchases" in Settings to recover access.',
      kind: "already-owned",
    };
  }
  if (/E_ITEM_UNAVAILABLE/i.test(msg)) {
    return {
      message: "This product is temporarily unavailable. Try again in a few minutes.",
      kind: "unavailable",
    };
  }
  if (/E_NETWORK_ERROR/i.test(msg)) {
    return { message: "Check your connection and try again.", kind: "generic" };
  }
  if (/E_BILLING_UNAVAILABLE/i.test(msg)) {
    return {
      message: "Billing is not available on this device.",
      kind: "generic",
    };
  }
  return { message: msg, kind: "generic" };
}

interface UseCheckoutRailReturn {
  busy: boolean;
  error: string | null;
  /** Item 6: Distinct kind for CONFLICT / account-mismatch errors. */
  errorKind: IapErrorKind | null;
  /** Start a purchase. Resolves on success; sets `error` on failure / cancel. */
  checkout: (input: {
    verticalSlug: string;
    jurisdictionCode: string | null;
    tier: CheckoutTier;
    /**
     * Native IAP product ID resolved from vertical.config.ts
     * iapProductIds. The caller already has the vertical loaded — we
     * don't refetch here just to look it up. On web, ignored.
     */
    iapProductId: string | null;
  }) => Promise<void>;
}

export function useCheckoutRail(): UseCheckoutRailReturn {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<IapErrorKind | null>(null);

  async function checkout(input: {
    verticalSlug: string;
    jurisdictionCode: string | null;
    tier: CheckoutTier;
    iapProductId: string | null;
  }): Promise<void> {
    setBusy(true);
    setError(null);
    setErrorKind(null);
    try {
      if (Platform.OS === "web") {
        await runStripeWebFlow(input, router);
        return;
      }

      if (!input.iapProductId) {
        // Native client tried to buy something the content package
        // hasn't priced for IAP. Either the SKU is missing from
        // App Store Connect / Play Console, or vertical.config.ts
        // never declared an iapProductIds entry for this tier.
        throw new Error(
          `No IAP product configured for ${input.verticalSlug}/${input.tier}`,
        );
      }

      let result;
      try {
        result = await iapBridge.purchase(input.iapProductId, input.tier);
      } catch (err) {
        // Item 7: classify native IAP errors before they reach the server
        const msg = err instanceof Error ? err.message : String(err);
        const classified = classifyIapError(msg);
        if (classified.kind === "cancel") {
          setError(null);
          setErrorKind(null);
        } else {
          setError(classified.message);
          setErrorKind(classified.kind);
        }
        return;
      }

      // Receipt verification + entitlement write on the server.
      // Sandbox/production routing is automatic per Apple's JWS env
      // field; Google ignores it.
      try {
        if (Platform.OS === "ios") {
          await trpc.iap.verifyApple.mutate({
            signedTransaction: result.receipt,
          });
        } else {
          await trpc.iap.verifyGoogle.mutate({
            purchaseToken: result.receipt,
            productId: result.productId,
          });
        }
      } catch (err) {
        // Item 6: CONFLICT = purchase linked to a different account
        if (
          err instanceof TRPCClientError &&
          (err.data as { code?: string } | undefined)?.code === "CONFLICT"
        ) {
          setError(err.message);
          setErrorKind("conflict");
          return;
        }
        throw err;
      }

      // Finish the platform transaction — only AFTER server-side
      // entitlement is durable, so a verifier-side crash doesn't lose
      // a paid purchase.
      await result.finish();

      // Mirror the Stripe redirect: drop the user on the success page
      // so the existing entitlement-confirm flow runs.
      router.push({
        pathname: "/checkout/success",
        params: {
          vertical: input.verticalSlug,
          ...(input.jurisdictionCode
            ? { jurisdiction: input.jurisdictionCode }
            : {}),
          provider: Platform.OS === "ios" ? "apple-iap" : "google-iap",
        },
      });
    } catch (err) {
      // Anonymous user hit a purchase (deep-linked straight to /paywall
      // without an account). Don't dump a raw "UNAUTHORIZED" on them —
      // route to sign-up so they can create an account and come back.
      // The selected vertical/jurisdiction is persisted by the store, so
      // returning to the paywall preserves context; pass them along too.
      if (isUnauthorized(err)) {
        setError("Create an account to purchase.");
        setErrorKind(null);
        router.push({
          pathname: "/signup",
          params: {
            vertical: input.verticalSlug,
            ...(input.jurisdictionCode
              ? { jurisdiction: input.jurisdictionCode }
              : {}),
          },
        });
        return;
      }
      const msg = err instanceof Error ? err.message : "checkout failed";
      const classified = classifyIapError(msg);
      if (classified.kind === "cancel") {
        setError(null);
        setErrorKind(null);
      } else {
        setError(classified.message);
        setErrorKind(classified.kind);
      }
    } finally {
      setBusy(false);
    }
  }

  return { busy, error, errorKind, checkout };
}

async function runStripeWebFlow(
  input: {
    verticalSlug: string;
    jurisdictionCode: string | null;
    tier: CheckoutTier;
  },
  router: ReturnType<typeof useRouter>,
): Promise<void> {
  const res = await trpc.checkout.createSession.mutate({
    verticalSlug: input.verticalSlug,
    jurisdictionCode: input.jurisdictionCode ?? undefined,
    tier: input.tier,
  });
  if (typeof window !== "undefined") {
    window.location.assign(res.url);
  } else {
    router.push(res.url);
  }
}

/**
 * Apple-required "Restore Purchases" CTA. Walks every active receipt
 * on-device, re-verifies each on the server. Returns the count of
 * successful re-verifications.
 */
export async function restorePurchases(): Promise<{
  restored: number;
  errors: string[];
}> {
  if (Platform.OS === "web") {
    throw new Error("Restore Purchases not available on web");
  }
  await iapBridge.initialize();
  const receipts = await iapBridge.restore();
  const errors: string[] = [];
  let restored = 0;
  for (const r of receipts) {
    try {
      if (Platform.OS === "ios") {
        await trpc.iap.verifyApple.mutate({ signedTransaction: r.receipt });
      } else {
        await trpc.iap.verifyGoogle.mutate({
          purchaseToken: r.receipt,
          productId: r.productId,
        });
      }
      restored += 1;
    } catch (err) {
      errors.push(err instanceof Error ? err.message : String(err));
    }
  }
  return { restored, errors };
}
